import { useState, useCallback } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { RouteProvider, useRoute } from '@/context/RouteContext';
import { RoutePanel } from '@/components/route/RoutePanel';
import { MapView } from '@/components/map/MapView';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const RoutePageContent = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  const {
    startAddress,
    subjectProperty,
    comparables,
    finishAddress,
    setRouteResult,
    setIsCalculating,
    setOptimizedOrder,
  } = useRoute();

  const calculateRoute = useCallback(async () => {
    if (!window.google?.maps) {
      toast.error('Google Maps not loaded yet');
      return;
    }

    setIsCalculating(true);
    
    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      // Helper to extract street address (remove city, state, zip)
      const getStreetAddress = (fullAddress) => {
        if (!fullAddress) return '';
        // Split by comma and take the first part (street address)
        return fullAddress.split(',')[0].trim();
      };
      
      // Build waypoints from subject and comparables
      const waypoints = [];
      const waypointLabels = [];
      
      if (subjectProperty.location) {
        waypoints.push({
          location: subjectProperty.location,
          stopover: true,
        });
        waypointLabels.push({ 
          type: 'subject', 
          label: 'Subject Property',
          address: getStreetAddress(subjectProperty.address)
        });
      }
      
      comparables.forEach((comp, index) => {
        if (comp.location) {
          waypoints.push({
            location: comp.location,
            stopover: true,
          });
          waypointLabels.push({ 
            type: 'address', 
            label: `Address ${index + 1}`,
            address: getStreetAddress(comp.address),
            addressNum: index + 1
          });
        }
      });

      const request = {
        origin: startAddress.location,
        destination: finishAddress.location,
        waypoints: waypoints,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
      };

      const result = await new Promise((resolve, reject) => {
        directionsService.route(request, (response, status) => {
          if (status === 'OK') {
            resolve(response);
          } else {
            reject(new Error(`Directions request failed: ${status}`));
          }
        });
      });

      setRouteResult(result);
      
      // Get optimized order
      const waypointOrder = result.routes[0].waypoint_order;
      const optimized = waypointOrder.map(index => waypointLabels[index]);
      setOptimizedOrder(optimized);
      
      toast.success('Route calculated successfully!');
      setDrawerOpen(false);
      
    } catch (error) {
      console.error('Route calculation error:', error);
      toast.error('Failed to calculate route. Please check your addresses.');
    } finally {
      setIsCalculating(false);
    }
  }, [startAddress, subjectProperty, comparables, finishAddress, setRouteResult, setIsCalculating, setOptimizedOrder]);

  return (
    <div className="h-screen w-screen overflow-hidden" data-testid="route-page">
      {/* Desktop Layout - Side by side */}
      <div className="hidden md:flex w-full h-full">
        {/* Left Panel */}
        <div className="w-[420px] shrink-0 border-r border-border h-full">
          <RoutePanel onCalculateRoute={calculateRoute} />
        </div>
        
        {/* Map */}
        <div className="flex-1 h-full">
          <MapView />
        </div>
      </div>

      {/* Mobile Layout - Full screen map with drawer */}
      <div className="flex md:hidden w-full h-full flex-col relative">
        {/* Map takes full screen */}
        <div className="flex-1 w-full">
          <MapView />
        </div>
        
        {/* Floating Button to open drawer - only show when drawer is closed */}
        <Button
          onClick={() => setDrawerOpen(true)}
          className={`fixed bottom-20 left-1/2 -translate-x-1/2 h-14 px-6 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-semibold shadow-xl rounded-full transition-all duration-300 ${
            drawerOpen ? 'opacity-0 pointer-events-none translate-y-10' : 'opacity-100 translate-y-0'
          }`}
          style={{ zIndex: 9998 }}
          data-testid="open-panel-btn"
        >
          <Menu className="w-5 h-5 mr-2" />
          Plan Route
        </Button>
        
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} modal={true}>
          <DrawerContent className="h-[80vh] max-h-[80vh] pb-16">
            <div className="h-full overflow-hidden">
              <RoutePanel onCalculateRoute={calculateRoute} />
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
};

export const RoutePage = () => {
  const [apiError, setApiError] = useState(null);

  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY') {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-zinc-100" data-testid="api-key-error">
        <div className="text-center p-8">
          <p className="text-lg font-semibold text-foreground">Google Maps API Key Required</p>
          <p className="text-sm text-muted-foreground mt-2">
            Please add your API key to the environment variables.
          </p>
        </div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-zinc-100">
        <div className="text-center p-8">
          <p className="text-lg font-semibold text-foreground">Error Loading Maps</p>
          <p className="text-sm text-muted-foreground mt-2">{apiError}</p>
        </div>
      </div>
    );
  }

  return (
    <APIProvider 
      apiKey={GOOGLE_MAPS_API_KEY}
      libraries={['places', 'routes']}
      onLoad={() => console.log('Google Maps API loaded successfully')}
      onError={(error) => {
        console.error('Google Maps API error:', error);
        setApiError('Failed to load Google Maps. Please check your connection and try again.');
      }}
    >
      <RouteProvider>
        <RoutePageContent />
      </RouteProvider>
    </APIProvider>
  );
};
